import type {
  NotificationChannel,
  NotificationGroup,
  NotificationKind,
  NotificationPayload,
  NotificationReason,
  NotificationView,
} from '@/lib/api/generated/model';
import type { TaskTab } from '@/features/tasks/links';

/**
 * One sentence per notification kind, in the product's words (spec 131
 * FR-008). Built from what the payload recorded at the time, so a task renamed
 * later does not rewrite an old notification. AI-drafted copy, listed for
 * review in quickstart.md.
 */
export function notificationSentence(notification: NotificationView): string {
  const p = notification.payload;
  if (notification.kind === 'escalated') return escalatedSentence(notification);
  const task = p.task_title ?? 'A task';
  const actor = notification.actor?.display_name ?? 'Someone';
  const step = p.step_name ?? 'a gate';
  switch (notification.kind) {
    case 'gate_reached':
      // Not "waiting on you": a gate with nobody assigned tells the task's
      // followers, and the notification does not say which case it is.
      return `${task} is waiting at ${step}`;
    case 'gate_assigned':
      return `${actor} assigned ${step} on ${task} to you`;
    case 'task_failed':
      return failedSentence(task, p.failure_reason);
    case 'task_blocked':
      // A run blocked for want of its person's own account is not a spend
      // limit's (spec 160): the service says which. Copy approved 2026-10-13.
      return p.blocked_by === 'personal_account'
        ? `${task} is blocked until its person links their own forge account`
        : `${task} is blocked by a spend limit`;
    case 'task_stopped':
      return `${actor} stopped ${task}`;
    case 'child_needs_attention':
      return `A sub-task of ${task} needs attention: ${p.child_task_title ?? 'a sub-task'}`;
    case 'task_done':
      return `${task} is done`;
    case 'children_finished':
      return `All sub-tasks of ${task} finished`;
    case 'linked_tasks_waiting':
      return waitingSentence(task, p.waiting_count);
    case 'scheduler_failed':
      return `The scheduler failed on ${task}`;
    case 'scheduler_stalled':
      return `The scheduler stopped on ${task}: nothing is running and tasks wait`;
    case 'task_commented':
      return `${actor} commented on ${task}`;
    case 'task_mentioned':
      return `${actor} mentioned you on ${task}`;
    case 'run_replied':
      // The asker only, inbox only (service spec 131 FR-007): the answer
      // itself is the excerpt beneath.
      return `Agent replied on ${task}`;
    case 'spend_limit_reached':
      return `${p.spend_scope === 'workflow' ? `${p.workflow_name ?? 'A workflow'}'s` : 'The workspace'} spend limit was reached in ${notification.workspace.name ?? 'a workspace'}`;
    case 'connection_failed':
      return connectionSentence(notification);
    case 'spend_limit_approaching': {
      const period = PERIOD[p.period_type ?? ''] ?? '';
      const workspace = notification.workspace.name ?? 'The workspace';
      return p.spend_scope === 'workflow'
        ? `${p.workflow_name ?? 'A workflow'}'s ${period}spend limit in ${workspace} is 80% used`
        : `${workspace} has used 80% of its ${period}spend limit`;
    }
    case 'trigger_failed':
      return `${p.trigger_name ? `Trigger ${p.trigger_name}` : 'A trigger'} failed in ${notification.workspace.name ?? 'a workspace'}`;
    // The person's own linked forge account (spec 159 US2). Copy reviewed and
    // approved 2026-10-12: the service's email words.
    case 'forge_account_broken':
      return `Your ${forgeName(p)} account needs relinking: runs use the shared account`;
    case 'forge_account_expiring':
      return `Your ${forgeName(p)} token expires ${expiryWords(p.expires_at)}: link a new one`;
    case 'forge_account_revoked':
      return `An administrator removed your linked ${forgeName(p)} account`;
    // A run did not start for want of the person's own account (spec 160).
    // Copy approved 2026-10-13: the service's email words.
    case 'forge_account_required':
      return `${task} did not start: link your ${forgeName(p)} account`;
    // An admin asks the person to link (spec 164). The service's email words.
    case 'forge_account_requested':
      return `An administrator asks you to ${p.forge_reason === 'not_linked' ? 'link' : 'relink'} your ${forgeName(p)} account`;
    // A workspace's people (service spec 154). The service's email words.
    case 'workspace_member_added':
      return `${actor} added you to ${notification.workspace.name ?? 'a workspace'} as ${p.member_role === 'owner' ? 'an owner' : 'a member'}`;
    case 'workspace_member_removed':
      return removalSentence(notification);
    // Someone asks to join (service spec 156). The service's email words.
    case 'workspace_access_requested':
      return `${actor} asked to join ${notification.workspace.name ?? 'a workspace'}`;
    // Its last active owner was deactivated (service spec 157); an admin names one.
    case 'workspace_without_owner':
      return `${notification.workspace.name ?? 'A workspace'} has no active owner`;
    // Access is enforced now (service spec 158): the owner checks who is in it.
    case 'workspace_members_review':
      return `Only members can change ${notification.workspace.name ?? 'this workspace'} now: check who is in it`;
  }
}

/**
 * Who went from a workspace and what their going cleared (service spec 154
 * US2): `removal_sentence` in the service's email copy, word for word.
 */
function removalSentence(notification: NotificationView): string {
  const p = notification.payload;
  const member = p.member_name ?? 'Someone';
  const workspace = notification.workspace.name ?? 'a workspace';
  const went = p.member_left
    ? `${member} left ${workspace}`
    : `${notification.actor?.display_name ?? 'Someone'} removed ${member} from ${workspace}`;
  const cleared = [
    countOf(p.gate_assignments_cleared, 'gate assignment'),
    countOf(p.step_assignees_cleared, 'workflow step'),
    countOf(p.follows_dropped, 'follow'),
  ].filter(Boolean);
  return cleared.length ? `${went}; cleared ${cleared.join(', ')}` : went;
}

function countOf(count: number | null | undefined, noun: string): string {
  if (!count) return '';
  return count === 1 ? `1 ${noun}` : `${String(count)} ${noun}s`;
}

/** The drawer tab a notification opens on: what it is about. */
export const NOTIFICATION_TAB: Record<NotificationKind, TaskTab> = {
  gate_reached: 'details',
  gate_assigned: 'details',
  task_failed: 'executions',
  task_blocked: 'executions',
  task_stopped: 'executions',
  child_needs_attention: 'details',
  task_done: 'results',
  children_finished: 'details',
  // The parent's drawer, where its waiting tasks and Start all are
  // (`deepLink.ts` lands it on the list).
  linked_tasks_waiting: 'details',
  // The parent's drawer, where the scheduler's line and its waiting tasks are
  // (spec 239 US3).
  scheduler_failed: 'details',
  scheduler_stalled: 'details',
  task_commented: 'comments',
  // Where a comment mention lands; a mention on a run's thread is routed by
  // `tabOf`, which says why.
  task_mentioned: 'comments',
  // The run's Conversation, at the reply (spec 149 FR-006; `deepLink.ts`).
  run_replied: 'executions',
  // connection_failed and spend_limit_approaching are workspace events: they
  // open their own pages (`deepLink.ts`), so these two tabs are never used.
  connection_failed: 'details',
  spend_limit_approaching: 'details',
  // Never used as such: an escalation opens what it escalates (`tabOf`).
  escalated: 'details',
  spend_limit_reached: 'executions',
  trigger_failed: 'details',
  // About the person's own account, not a task: they open the Commits
  // settings (`deepLink.ts`), so these tabs are never used.
  forge_account_broken: 'details',
  forge_account_expiring: 'details',
  forge_account_revoked: 'details',
  forge_account_required: 'details',
  forge_account_requested: 'details',
  // About a workspace's people, not a task: they open the workspace or its
  // Settings (`deepLink.ts`), so these tabs are never used.
  workspace_member_added: 'details',
  workspace_member_removed: 'details',
  workspace_access_requested: 'details',
  workspace_without_owner: 'details',
  workspace_members_review: 'details',
};

/**
 * Why a run failed, in a few words (spec 135 US3), from the run's recorded
 * error as the service classified it. The same words as the service's email.
 */
const FAILURE_CAUSE: Record<
  Exclude<FailureReason, 'timed_out' | 'other'>,
  string
> = {
  credentials_rejected: 'credentials rejected',
  provider_unavailable: 'model provider unavailable',
  rate_limited: 'rate-limited',
  out_of_memory: 'out of memory',
  turn_limit: 'turn limit reached',
  not_started: 'never started',
};

/** The categories the service sends (service spec 120 FR-008). */
type FailureReason =
  | 'timed_out'
  | 'credentials_rejected'
  | 'provider_unavailable'
  | 'rate_limited'
  | 'out_of_memory'
  | 'turn_limit'
  | 'not_started'
  | 'other';

/** A reason this client does not know, or none, reads as a plain failure. */
function failedSentence(task: string, reason: string | null | undefined) {
  if (reason === 'timed_out') return `Run timed out on ${task}`;
  const cause =
    reason && reason in FAILURE_CAUSE
      ? FAILURE_CAUSE[reason as keyof typeof FAILURE_CAUSE]
      : undefined;
  return cause ? `Run failed on ${task}: ${cause}` : `Run failed on ${task}`;
}

/**
 * Created tasks waiting for someone to start them (spec 140 US3). The count
 * is the service's, refreshed while the notification is open. Without a count
 * (`waiting_count` missing or 0 — not sent by the service for an open notice,
 * which resolves once none waits) the sentence names no number rather than a
 * wrong one.
 */
function waitingSentence(task: string, count: number | null | undefined) {
  if (count === 1) return `1 task from ${task} is ready to start`;
  return count
    ? `${String(count)} tasks from ${task} are ready to start`
    : `Tasks from ${task} are ready to start`;
}

/** Why a notification reached this person (spec 135 US2). */
const REASON_LINE: Record<NotificationReason, string> = {
  mentioned: 'You were mentioned',
  assignee: "You're assigned to this gate",
  follower: 'You follow this task',
  workspace_owner: 'You own this workspace',
  org_admin: "You're an organization admin",
  escalation: 'Escalated: nobody picked it up',
  asked: 'You asked the agent',
  // Copy reviewed and approved 2026-10-12 (spec 159 US2).
  own_account: 'about your own account',
  // Being added to a workspace (service spec 154).
  membership: 'About your place in this workspace',
};

/** `null` for notifications from before reasons were recorded. */
export function notificationReason(
  notification: NotificationView,
): string | null {
  return notification.reason ? REASON_LINE[notification.reason] : null;
}

/** Shown on a row whose cause is over (spec 135 US1). */
export const RESOLVED_LABEL = 'Resolved';

/** The chip that says a notification is critical (spec 141 US1). */
export const CRITICAL_LABEL = 'Critical';

/** The words of the settings page (spec 142's copy table). */
export const SETTINGS_COPY = {
  saved: 'Saved as you change them.',
  alwaysOn: 'Inbox is always on',
  zone: (zone: string) => `Your time zone: ${zone}`,
  noMutes: 'No workspace is muted.',
  mutePicker: 'Mute a workspace…',
  noWorkspaceMatch: 'No workspace matches.',
  mute: (workspace: string) => `Mute ${workspace}`,
  unmute: (workspace: string) => `Unmute ${workspace}`,
  muteFailed: (workspace: string, muted: boolean) =>
    `${workspace} could not be ${muted ? 'muted' : 'unmuted'}.`,
  workspacesUnavailable: 'Workspaces could not be loaded.',
  /**
   * An email switch with kinds of its own (service spec 154 FR-006): Workspace
   * email is off, yet being added to a workspace is emailed. Spec 196 US6: a
   * switch of its own, named for what it does to the reader, rather than a
   * sentence with an underlined way out.
   */
  emailExceptionSwitch: (kinds: readonly NotificationKind[]) =>
    `Email me ${kinds
      .map((kind) => DEFAULT_KIND_PHRASE[kind] ?? kind)
      .join(' and ')}`,
  /** Under it while untouched: what it does, against the row's own switch. */
  emailExceptionApart: (title: string, on: boolean) =>
    on
      ? `On, though ${title} email is off.`
      : `Off, though ${title} email is on.`,
  /**
   * Under it once changed: any choice for the row covers these kinds too, and
   * nothing stored can set them apart again, so it shows the row's value.
   */
  emailExceptionFollows: 'Follows the Email switch.',
} as const;

/** How a kind with its own email default reads in that switch (spec 187). */
const DEFAULT_KIND_PHRASE: Partial<Record<NotificationKind, string>> = {
  workspace_member_added: 'when I’m added to a workspace',
};

/** The settings page's rows (spec 142 US2): one per group. */
export const SETTINGS_GROUPS: {
  group: NotificationGroup;
  title: string;
  /** One description per group (spec 142 US2), email folded in. */
  detail: (zone: string) => string;
  /** No alert switch: the group never alerts (spec 136). */
  noAlerts?: boolean;
}[] = [
  {
    group: 'needs_you',
    title: 'Needs you',
    detail: () =>
      'A task waiting at a gate for you, or a task you follow failing, blocked or stopped. Email after 5 minutes if still unread.',
  },
  {
    group: 'outcome',
    title: 'Outcomes',
    detail: (zone) =>
      `Tasks you follow reaching Done, their sub-tasks finishing, and new comments. Email as a daily digest at 08:00 your time (${zone}).`,
    noAlerts: true,
  },
  {
    group: 'workspace',
    title: 'Workspace',
    detail: () =>
      'Spend limits reached, triggers and connections failing, and people leaving, in workspaces you own or administer — and being added to one.',
  },
];

/** The table's columns, and what each switch's name says about it. */
export const SETTINGS_CHANNELS: {
  channel: NotificationChannel;
  heading: string;
  says: string;
}[] = [
  { channel: 'in_app', heading: 'Inbox', says: 'in your inbox' },
  { channel: 'email', heading: 'Email', says: 'by email' },
  { channel: 'alert', heading: 'Alerts', says: 'as alerts' },
];

/** Where the settings live (spec 142). */
export const SETTINGS_PATH = '/settings/notifications';

/** Where every notification can be found (spec 147). */
export const NOTIFICATIONS_PATH = '/notifications';

/** The words of the restyled inbox (spec 141's copy table, spec 147's footer). */
export const INBOX_COPY = {
  today: 'Today',
  earlier: 'Earlier',
  seeAll: 'See all notifications',
} as const;

/** The words of the notifications page (spec 147's copy). */
export const PAGE_COPY = {
  title: 'Notifications',
  settings: 'Notification settings',
  paletteEntry: 'Go to notifications',
  showOlder: 'Show older',
  retention: 'Notifications are kept for 90 days.',
  emptyFiltered: 'No notifications match these filters',
  clearFilters: 'Clear filters',
  needsYouResolved: 'Needs you lists only open notifications.',
  filters: 'Filters',
  workspace: 'Workspace',
  type: 'Type',
  date: 'Date',
  state: 'State',
  from: 'From',
  to: 'To',
  allWorkspaces: 'All workspaces',
  markRead: 'Mark read',
  markUnread: 'Mark unread',
  archive: 'Archive',
  unarchive: 'Unarchive',
  selectPage: 'Select all on this page',
  selectMatching: (n: number) => `Select all ${String(n)} matching`,
  selected: (n: number) => `${String(n)} selected`,
  markAllMatching: 'Mark all matching as read',
  archiveAllMatching: 'Archive all matching',
  confirmArchive: (n: number) =>
    `Archive ${String(n)} ${n === 1 ? 'notification' : 'notifications'}?`,
  confirmRead: (n: number) =>
    `Mark ${String(n)} ${n === 1 ? 'notification' : 'notifications'} as read?`,
  arrivals: (n: number) => `${String(n)} new · Show`,
} as const;

/**
 * A trigger that keeps failing is one notification that counts (spec 135
 * US4): "Failed 12 times since 09:00". A payload without a count is a single
 * failure, so this is `null` until it has failed twice.
 */
export function triggerRepeatLine(
  payload: NotificationPayload,
  since: string,
): string | null {
  const count = payload.failure_count ?? 1;
  if (count < 2) return null;
  const time = new Date(since).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `Failed ${String(count)} times since ${time}`;
}

/** The alert's words (spec 136, approved 2026-10-06). */
export const ALERT_COPY = {
  open: 'Open',
  dismiss: 'Dismiss',
  critical: 'Critical',
  more: (n: number) => `+${String(n)} more`,
} as const;

/** "1 hour", "4 hours" (spec 138). */
function hours(n: number): string {
  return n === 1 ? '1 hour' : `${String(n)} hours`;
}

/**
 * Something that needs action, left unattended past the organization's delay
 * (spec 138 US2). Read from what it escalates: the original's kind and
 * payload travel with it. The same words as the service's email copy.
 */
function escalatedSentence(notification: NotificationView): string {
  const p = notification.payload;
  const waited = hours(p.waited_hours ?? 0);
  const task = p.task_title ?? 'A task';
  switch (p.escalated_kind) {
    case 'gate_reached':
    case 'gate_assigned':
      return `${task} has waited at ${p.step_name ?? 'a gate'} for ${waited}`;
    case 'task_failed':
      return `Run failed on ${task} ${waited} ago, and nobody has picked it up`;
    case undefined:
    case null:
    case 'escalated':
      return `${task} needs attention, unattended for ${waited}`;
    default:
      // The original's own sentence, from its payload, which the escalation
      // carries whole. Only Action-required and Critical kinds escalate, and
      // none that reaches here names an actor (a stop, which does, is a
      // Warning), so the escalation having none of its own loses nothing.
      // A blocked run is not "Run failed": it did not fail (spec 138).
      return `${notificationSentence({
        ...notification,
        kind: p.escalated_kind,
      })}, unattended for ${waited}`;
  }
}

/**
 * The drawer tab a notification opens on; an escalation opens its original's.
 *
 * The one place thread routing is decided: anything about a message on a run's
 * thread — a reply, or a mention there — names that message in its payload
 * (`threadMessageOf`), and opens the Runs tab, where the thread is (spec 149).
 * A `task_mentioned` without one is a comment mention, and `NOTIFICATION_TAB`
 * sends it to Comments.
 */
export function tabOf(notification: NotificationView): TaskTab {
  if (threadMessageOf(notification)) return 'executions';
  const kind =
    notification.kind === 'escalated'
      ? (notification.payload.escalated_kind ?? 'escalated')
      : notification.kind;
  return NOTIFICATION_TAB[kind];
}

/** "I'm on it" (spec 138 US1), and what the row says afterwards. */
export const ACKNOWLEDGE_COPY = {
  action: "I'm on it",
  done: "You're on it",
} as const;

/** Whether "I'm on it" means anything here: open, unacknowledged, and needing action. */
export function canAcknowledge(notification: NotificationView): boolean {
  return (
    (notification.severity === 'action_required' ||
      notification.severity === 'critical') &&
    notification.resolved_at == null &&
    notification.acknowledged_at == null
  );
}

/** The provider as people name it (service spec 124's email copy). */
const PROVIDER: Partial<Record<string, string>> = {
  github: 'GitHub',
  gitlab: 'GitLab',
  jira: 'Jira',
  oci_registry: 'Container registry',
};

/** Why a connection is failing, in a few words (service spec 124). */
export const CONNECTION_CAUSE: Partial<Record<string, string>> = {
  credentials_rejected: 'credentials rejected',
  access_denied: 'access denied',
  test_failed: 'the connection test failed',
};

/** "monthly " — with its space — or nothing for a lifetime limit. */
const PERIOD: Partial<Record<string, string>> = {
  daily: 'daily ',
  weekly: 'weekly ',
  monthly: 'monthly ',
};

/**
 * "GitHub connection Org repos in Payments stopped working: credentials
 * rejected" (spec 139). A type this client does not name reads as stored, and
 * a cause it does not know reads as having stopped responding — the email's
 * words for both.
 */
function connectionSentence(notification: NotificationView): string {
  const p = notification.payload;
  const provider = p.provider ? (PROVIDER[p.provider] ?? p.provider) : 'A';
  const name = p.connection_name ? ` ${p.connection_name}` : '';
  const cause =
    (p.connection_reason ? CONNECTION_CAUSE[p.connection_reason] : undefined) ??
    'it stopped responding';
  return `${provider} connection${name} in ${notification.workspace.name ?? 'a workspace'} stopped working: ${cause}`;
}

/**
 * "GitLab (gitlab.corp.example)": the forge and the host a linked account is
 * for (spec 159). A provider this client does not name reads as "forge", as
 * in the service's email.
 */
function forgeName(payload: NotificationPayload): string {
  const provider =
    (payload.provider ? PROVIDER[payload.provider] : undefined) ?? 'forge';
  return payload.forge_host ? `${provider} (${payload.forge_host})` : provider;
}

/** "on 2026-11-01", or "soon" when the token's expiry was not recorded. */
function expiryWords(expiresAt: string | null | undefined): string {
  const date = expiresAt?.slice(0, 10);
  return date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? `on ${date}` : 'soon';
}

/**
 * The run and message a notification is about, when it is about a message on
 * a run's thread (service spec 131): `run_replied`, and a mention posted there.
 */
export function threadMessageOf(
  notification: NotificationView,
): { executionId: string; messageId: string } | null {
  const { execution_id: executionId, thread_message_id: messageId } =
    notification.payload;
  return executionId && messageId ? { executionId, messageId } : null;
}
